/* Drawing kit for the sheet pages. Every page is one SVG in the PDF's own coordinate
   space (612 x 792 points, measured from the official sheets), so it matches the
   printed page at any size: on screen, in the builder preview and in print.
   Colors come from CSS classes that read the --cs-* tokens. */

import { KeyboardEvent, ReactNode, useLayoutEffect, useRef } from 'react';
import { ChevronsUp, Settings } from 'lucide-react';
import { SECTION_NAMES, SheetSection } from './content/sheetText';

export const PAGE_W = 612;
export const PAGE_H = 792;

export type Box = [x: number, y: number, w: number, h: number];

export function SheetSvg({ label, className, view, children }: { label: string; className?: string; view?: Box; children: ReactNode }) {
  return (
    <article className={['cs-page', className].filter(Boolean).join(' ')} aria-label={label}>
      <svg className="cs-svg" viewBox={view ? view.join(' ') : `0 0 ${PAGE_W} ${PAGE_H}`} xmlns="http://www.w3.org/2000/svg">
        <rect className="s-paper" width={PAGE_W} height={PAGE_H} />
        {children}
      </svg>
    </article>
  );
}

type Tone = 'ink' | 'title' | 'soft' | 'muted' | 'band' | 'frame' | 'label';

interface TxtProps {
  x: number;
  y: number;
  size: number;
  weight?: number;
  italic?: boolean;
  anchor?: 'middle' | 'end';
  tone?: Tone;
  children: ReactNode;
}

/** One line of text at a baseline. Headings use weight 900 in capitals, as the sheets do. */
export function Txt({ x, y, size, weight = 400, italic, anchor, tone = 'ink', children }: TxtProps) {
  return (
    <text x={x} y={y} fontSize={size} fontWeight={weight} fontStyle={italic ? 'italic' : undefined} textAnchor={anchor} className={`t-${tone}`}>
      {children}
    </text>
  );
}

/**
 * Scales a text box's content down in 2.5% steps, to three quarters at most, until it fits `h`.
 * The box grows by the same factor first, so the smaller text rewraps at the full width.
 */
export function fitToBox(el: HTMLElement, w: number, h: number) {
  const scaleTo = (s: number) => {
    el.style.width = `${w / s}px`;
    el.style.height = `${h / s}px`;
    el.style.transformOrigin = '0 0';
    el.style.transform = s < 1 ? `scale(${s})` : '';
  };
  scaleTo(1);
  for (let step = 1; step <= 10 && el.scrollHeight > el.clientHeight + 0.5; step++) scaleTo(1 - step * 0.025);
}

/**
 * HTML inside the page, for text that wraps. CSS px inside equal points on the page.
 * `fit` shrinks long text (Hope and class features, card texts, guide columns) to fit its box.
 */
export function Html({ x, y, w, h, className, size, fit, children }: { x: number; y: number; w: number; h: number; className?: string; size?: number; fit?: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const fitted = useRef('');
  useLayoutEffect(() => {
    const el = ref.current;
    if (!fit || !el) return;
    // Measuring forces a layout, so only refit when the text or the box changes.
    const key = `${w} ${h} ${el.textContent}`;
    if (fitted.current === key) return;
    fitted.current = key;
    fitToBox(el, w, h);
    document.fonts?.ready.then(() => fitToBox(el, w, h));
  });
  return (
    <foreignObject x={x} y={y} width={w} height={h}>
      <div ref={ref} className={['cs-html', className].filter(Boolean).join(' ')} style={size ? { fontSize: size } : undefined}>
        {children}
      </div>
    </foreignObject>
  );
}

/** A single-line write-in value whose baseline sits just above `base`. */
export function Value({ x, base, w, size = 8.5, center, children }: { x: number; base: number; w: number; size?: number; center?: boolean; children?: ReactNode }) {
  return (
    <Html x={x} y={base - size * 1.2} w={w} h={size * 1.5} size={size} className={`cs-value${center ? ' is-center' : ''}`}>
      {children}
    </Html>
  );
}

/** A rectangle path with cut (chamfered) corners. */
export const cutBox = (x0: number, y0: number, x1: number, y1: number, c: number) =>
  `M${x0 + c} ${y0}H${x1 - c}L${x1} ${y0 + c}V${y1 - c}L${x1 - c} ${y1}H${x0 + c}L${x0} ${y1 - c}V${y0 + c}Z`;

/** A rounded frame; `open` leaves the bottom edge out, as on the sheet's stacked boxes. */
export const roundBox = (x0: number, y0: number, x1: number, y1: number, r: number, open = false) =>
  `M${x0 + r} ${y1}Q${x0} ${y1} ${x0} ${y1 - r}V${y0 + r}Q${x0} ${y0} ${x0 + r} ${y0}H${x1 - r}Q${x1} ${y0} ${x1} ${y0 + r}V${y1 - r}Q${x1} ${y1} ${x1 - r} ${y1}${open ? '' : 'Z'}`;

/** A write-in line with its ends turned up. */
export function WriteLine({ x0, x1, y, tone = 'rule' }: { x0: number; x1: number; y: number; tone?: 'rule' | 'faint' }) {
  return <path className={`o-${tone}`} d={`M${x0} ${y}L${x0 + 3.2} ${y + 3.2}H${x1 - 3.2}L${x1} ${y}`} />;
}

/** A dotted divider. */
export function Dots({ x, y0, y1, tone = 'faint' }: { x: number; y0: number; y1: number; tone?: 'faint' | 'rule' }) {
  return <line className={`o-${tone} o-dots`} x1={x} y1={y0} x2={x} y2={y1} />;
}

interface BannerProps {
  x0: number;
  x1: number;
  y: number;
  h?: number;
  title: ReactNode;
  /** Bar showing above the plate; null for none. */
  tab?: [number, number] | null;
  tabRaise?: number;
  /** How far the tab shows below the plate; negative ends it behind the plate (Class Feature). */
  tabDrop?: number;
  /** The tab's corner cut. */
  tabCut?: number;
  wings?: [number, number];
  align?: 'center' | 'left';
  size?: number;
}

const round2 = (v: number) => Math.round(v * 100) / 100;

/**
 * The banner's three shapes, measured from the PDFs: the tab and the bar are cut-corner boxes (3.1pt cuts),
 * and the plate's 1pt outline lies inside its box, so its centre line runs half a point in (on the 45 degree
 * points too). `y`, `h` and the x values are the outer edges.
 */
export function bannerPaths({ x0, x1, y, h = 15.1, tab, tabRaise = 1, tabDrop = 1.1, tabCut = 3.1, wings }: Omit<BannerProps, 'title' | 'align' | 'size'>) {
  const mid = y + h / 2;
  const cx = (x0 + x1) / 2;
  const [wx0, wx1] = wings ?? [x0 - 4.4, x1 + 4.4];
  const [tx0, tx1] = tab ?? [cx - 47, cx + 47];
  const inset = 0.5;
  const corner = round2(h / 2 + inset * Math.tan(Math.PI / 8));
  const point = round2(inset * Math.SQRT2);
  return {
    tab: tab === null ? null : cutBox(tx0, round2(y - tabRaise), tx1, round2(y + h + tabDrop), tabCut),
    bar: cutBox(wx0, round2(mid - 4.4), wx1, round2(mid + 4.4), 3.1),
    plate: `M${round2(x0 + corner)} ${y + inset}H${round2(x1 - corner)}L${round2(x1 - point)} ${round2(mid)}L${round2(x1 - corner)} ${round2(y + h - inset)}H${round2(x0 + corner)}L${round2(x0 + point)} ${round2(mid)}Z`,
  };
}

/** The sheets' section banner: a pointed plate on a bar, with a darker tab above it. */
export function Banner({ x0, x1, y, h = 15.1, title, align = 'center', size = 10, ...shape }: BannerProps) {
  const { tab, bar, plate } = bannerPaths({ x0, x1, y, h, ...shape });
  const cx = (x0 + x1) / 2;
  return (
    <g>
      {tab && <path className="s-frame" d={tab} />}
      <path className="s-frame" d={bar} />
      <path className="s-paper o-frame" d={plate} />
      <Txt x={align === 'center' ? cx : x0 + 14} y={y + h * 0.76} size={size} weight={900} anchor={align === 'center' ? 'middle' : undefined} tone="title">
        {title}
      </Txt>
    </g>
  );
}

/** The dark label plate with 1.6pt cut corners (EVASION, ARMOR, LEVEL, trait names). */
export function Pill({ x, y, w, h, label, size, align = 'center' }: { x: number; y: number; w: number; h: number; label: string; size: number; align?: 'center' | 'left' }) {
  return (
    <g>
      <path className="s-band" d={cutBox(x, y, x + w, y + h, 1.6)} />
      <Txt x={align === 'center' ? x + w / 2 : x + 2.9} y={y + h * 0.78} size={size} weight={900} tone="band" anchor={align === 'center' ? 'middle' : undefined}>
        {label}
      </Txt>
    </g>
  );
}

/** A trait shield's outline, its top left at 0 0 (45 x 46 points). */
export const SHIELD_PATH = 'M0.5 0H44V20.7L41.2 23.9V39L22.3 45.8L3.4 39.3V23.8L0.5 20.6Z';

/**
 * One trait as the sheet draws it: name pill, mark circle, shield with its ribbons, value and
 * verbs. `x` is the pill's left edge; the drawing spans y 67 to 143 of the sheet.
 */
export function TraitShield({ x, label, value, verbs, marked }: { x: number; label: string; value: string; verbs: string[]; marked?: boolean }) {
  const b = x + 7;
  return (
    <>
      <path className="s-frame" d={`M${b + 10.7} 108.1L${b + 0.5} 97.8V85.1H${b + 21}V103.7Z`} />
      <path className="s-frame" d={`M${b + 23.6} 103.7V85.1H${b + 44.1}V97.8L${b + 33.9} 108.1Z`} />
      <path className="s-paper o-frame" transform={`translate(${b} 70.9)`} d={SHIELD_PATH} />
      <path className="o-faint o-thin" d={`M${b + 3.9} 74.2H${b + 40.6}V90.2L${b + 37.8} 93.5V107.3L${b + 24.9} 112.1L${b + 22.3} 111.2L${b + 19.6} 112.1L${b + 6.8} 107.7V93.3L${b + 3.9} 90.1Z`} />
      <Pill x={x} y={68.8} w={58.9} h={9} label={label} size={6.2} align="left" />
      <circle className={`s-paper o-frame${marked ? ' is-marked' : ''}`} cx={x + 56.8} cy={73.35} r={6.4} {...(marked && { role: 'img', 'aria-label': `${label} marked` })} />
      <Txt x={b + 22.25} y={101} size={16} weight={800} anchor="middle">{value}</Txt>
      {verbs.map((v, k) => (
        <Txt key={v} x={b + 22.25} y={125.3 + k * 8.05} size={6.5} tone="soft" anchor="middle">{v}</Txt>
      ))}
    </>
  );
}

/** The Evasion shield's outline: a round dome over a squared base. */
export const EVASION_PATH = 'M16.1 124.3V103.2L19.2 99.6V97.6C19.2 89.2 24.5 81.7 32.3 78.8L32.7 78.6C34.9 77.8 37.3 77.4 39.6 77.4C41.9 77.4 44.1 77.8 46.2 78.5C54.3 81.3 59.7 88.9 59.7 97.5V99.9L62.7 103.3V124.3Z';

/** The sheet's Evasion shield, with the class's starting Evasion under it. */
export function EvasionShield({ value, start }: { value: number | string; start?: number | string }) {
  return (
    <>
      <path className="s-frame" d="M16 96.8L27 85.5L38 90V110.1H16Z" />
      <path className="s-frame" d="M62.8 96.6L51.8 85.5L40.8 90.2V110.1H62.8Z" />
      <path className="s-paper o-frame" d="M39.4 97.3L19.8 116.9L33.2 130.3H45.6L59.1 116.9Z" />
      <path className="s-paper o-frame" d={EVASION_PATH} />
      <path className="o-faint o-thin" d="M39.6 80.8C41.5 80.8 43.3 81.1 45.1 81.8C51.8 84.1 56.3 90.4 56.3 97.5V101.2L59.3 104.6V120.9H19.5V104.4L22.6 100.9V97.6C22.6 90.7 27 84.4 33.5 82L33.9 81.9C35.7 81.2 37.7 80.8 39.6 80.8Z" />
      <Txt x={39.4} y={107} size={18} weight={800} anchor="middle">{value}</Txt>
      <Pill x={13.6} y={116.9} w={51.6} h={9.7} label="EVASION" size={7.7} />
      {start !== undefined && <Txt x={39.3} y={139.8} size={7} tone="muted" anchor="middle">Start at {start}</Txt>}
    </>
  );
}

interface SlotProps {
  label: string;
  marked: boolean;
  locked?: boolean;
  onToggle?: () => void;
  children: ReactNode;
}

/** One markable slot. Clickable and keyboard reachable when the sheet is in play. */
export function Slot({ label, marked, locked, onToggle, children }: SlotProps) {
  const live = !!onToggle && !locked;
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onToggle?.();
  };
  return (
    <g
      className={`cs-slot${marked ? ' is-marked' : ''}${locked ? ' is-locked' : ''}${live ? ' is-live' : ''}`}
      role={live ? 'button' : undefined}
      tabIndex={live ? 0 : undefined}
      aria-label={live ? label : undefined}
      aria-pressed={live ? marked : undefined}
      onClick={live ? onToggle : undefined}
      onKeyDown={live ? onKey : undefined}
    >
      {children}
    </g>
  );
}

interface TrackProps {
  label: string;
  total: number;
  /** Slots past this are drawn dashed: not gained yet. */
  open?: number;
  marked: number;
  onSet?: (count: number) => void;
  slot: (i: number) => ReactNode;
}

/** A row of slots. Clicking a marked slot clears it and every slot after it. */
export function Track({ label, total, open = total, marked, onSet, slot }: TrackProps) {
  return (
    <g role="group" aria-label={label}>
      {Array.from({ length: total }, (_, i) => (
        <Slot key={i} label={`${label} ${i + 1}`} marked={i < marked} locked={i >= open} onToggle={onSet && (() => onSet(i < marked ? i : i + 1))}>
          {slot(i)}
        </Slot>
      ))}
    </g>
  );
}

/** The page's header tab: a dark slanted band with the title over its subtitle. */
export function HeadTab({ x = 0, w, h, slant = 34, title, subtitle, textX = 16 }: { x?: number; w: number; h: number; slant?: number; title: string; subtitle: string; textX?: number }) {
  return (
    <g>
      <path className="s-band" d={`M${x} 0H${x + w}L${x + w - slant} ${h}H${x}Z`} />
      <Txt x={x + textX} y={h * 0.49} size={19} weight={900} tone="band">{title.toUpperCase()}</Txt>
      <Txt x={x + textX} y={h * 0.71} size={11} weight={300} tone="band">{subtitle.toUpperCase()}</Txt>
    </g>
  );
}

/** Diagonal stripes on the side of a tile sheet, which is laid beside the character sheet. */
export function TileStripes({ w }: { w: number }) {
  return (
    <g>
      <defs>
        <pattern id="cs-stripes" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line className="o-faint" x1="0" y1="0" x2="0" y2="6" />
        </pattern>
      </defs>
      <rect x={0} y={0} width={w} height={PAGE_H} fill="url(#cs-stripes)" />
      <line className="o-faint o-thick" x1={w} y1={0} x2={w} y2={PAGE_H} />
    </g>
  );
}

export function PageFooter({ hf, x = 9.9 }: { hf?: boolean; x?: number }) {
  // The boxes above end about 4pt over the printed line, so the second line goes below it.
  return (
    <>
      <Txt x={x} y={780.2} size={7} weight={800} tone="soft">dhtools</Txt>
      <Txt x={x} y={787.4} size={6.3} tone="soft">Daggerheart © Darrington Press {hf ? 2026 : 2025}</Txt>
    </>
  );
}

/** Gold frames over the areas the builder's current step fills in. */
const FOCUS_MARGIN = 10;
const FOCUS_MIN_W = 260;

/**
 * The part of a page to show in the builder's close-up: the lit areas with a margin, at least
 * FOCUS_MIN_W wide so the surroundings show, kept on the page. `shift` moves areas drawn in a
 * bleed group onto the page. Undefined when nothing on the page is lit.
 */
export function focusBox(boxes: Partial<Record<SheetSection, Box>>, on: SheetSection[], shift = 0): Box | undefined {
  const lit = on.map((s) => boxes[s]).filter((b): b is Box => !!b);
  if (!lit.length) return undefined;
  let x0 = Math.max(0, Math.min(...lit.map((b) => b[0])) + shift - FOCUS_MARGIN);
  let x1 = Math.min(PAGE_W, Math.max(...lit.map((b) => b[0] + b[2])) + shift + FOCUS_MARGIN);
  const y0 = Math.max(0, Math.min(...lit.map((b) => b[1])) + shift - FOCUS_MARGIN);
  const y1 = Math.min(PAGE_H, Math.max(...lit.map((b) => b[1] + b[3])) + shift + FOCUS_MARGIN);
  if (x1 - x0 < FOCUS_MIN_W) {
    x0 = Math.max(0, Math.min((x0 + x1 - FOCUS_MIN_W) / 2, PAGE_W - FOCUS_MIN_W));
    x1 = x0 + FOCUS_MIN_W;
  }
  return [x0, y0, x1 - x0, y1 - y0];
}

export function Highlights({ boxes, on }: { boxes: Partial<Record<SheetSection, Box>>; on: SheetSection[] }) {
  return (
    <>
      {on.map((s) => {
        const b = boxes[s];
        return b ? <rect key={s} className="cs-hl" x={b[0]} y={b[1]} width={b[2]} height={b[3]} rx={6} /> : null;
      })}
    </>
  );
}

// One open hand as the PDFs draw it (the Bard sheet's left hand, its top left at 0 0, 16.7 x 21 points): the palm,
// the outline with the finger slits, and the inner outline and finger lines. The right hand mirrors it.
const HAND_PALM = 'M5.7 19.2L3.9 17.7C3.4 17.2 3 16.7 2.8 16L1.4 11.8L0.6 6.2C0.6 5.6 0.9 5.1 1.4 5L2.3 4.7L2 3.4C1.9 2.9 2.3 2.3 2.8 2.2L4.1 1.8L4 1.5L4.1 1.4C4.2 1.1 4.4 1 4.6 0.9L5.8 0.6C5.9 0.5 6 0.5 6.1 0.5C6.5 0.5 6.9 0.8 7.1 1.1L7.2 1.7L8.3 1.4C8.4 1.4 8.5 1.3 8.6 1.3C9.1 1.3 9.5 1.6 9.6 2L12.7 9.9L13.6 8.1C13.8 7.8 14.1 7.7 14.2 7.6C14.3 7.6 14.4 7.6 14.5 7.6C14.7 7.6 14.9 7.6 15 7.7L15.6 8.1C16 8.3 16.1 8.8 16 9.2L14.2 15.1C14.1 15.6 13.9 16 13.6 16.3L13 17.1L13.3 18.2L6 20.4ZM5.7 19.2';
const HAND_EDGE = 'M6.1 0C5.9 0 5.8 0 5.6 0.1L4.5 0.4C4.1 0.5 3.8 0.8 3.7 1.2L3.5 1.4L3.5 1.5L2.7 1.7C1.9 1.9 1.4 2.7 1.5 3.5L1.7 4.4L1.3 4.5C0.5 4.7 0 5.5 0.2 6.3L0.9 11.8L0.9 11.9L0.9 11.9L2.4 16.2C2.6 16.9 3 17.6 3.6 18.1L5.3 19.5L5.4 20L5.7 21L6.7 20.7L12.9 18.8L13.9 18.6L13.6 17.6L13.5 17.2L14 16.6C14.3 16.2 14.6 15.8 14.7 15.3L16.5 9.4C16.7 8.7 16.4 8 15.8 7.6L15.3 7.3C15 7.2 14.8 7.1 14.5 7.1C14.4 7.1 14.2 7.1 14.1 7.1L14 7.2L14 7.2C13.6 7.4 13.3 7.6 13.1 7.9L12.8 8.7L10.1 1.9C9.9 1.2 9.3 0.8 8.6 0.8C8.4 0.8 8.3 0.9 8.1 0.9L7.6 1.1L7.6 1.1L7.5 1L7.5 0.9C7.2 0.4 6.7 0 6.1 0M6.1 1C6.3 1 6.5 1.2 6.6 1.4L8.8 8.4L9.5 8.2L7.6 2.1L8.4 1.9C8.5 1.8 8.5 1.8 8.6 1.8C8.8 1.8 9.1 2 9.2 2.2L12.4 10.4C12.4 10.5 12.5 10.6 12.7 10.6C12.7 10.6 12.7 10.6 12.7 10.6C12.8 10.6 12.9 10.6 13 10.4L14 8.4C14.1 8.2 14.2 8.2 14.4 8.1C14.4 8.1 14.5 8.1 14.5 8.1C14.6 8.1 14.7 8.1 14.8 8.2L15.3 8.5C15.5 8.6 15.6 8.9 15.5 9.1L13.7 15C13.6 15.3 13.5 15.7 13.2 16L12.5 16.8C12.4 16.9 12.4 17.1 12.5 17.2L12.7 17.9L6.4 19.7L6.2 19.1C6.2 19 6.1 19 6.1 18.9L4.3 17.3C3.8 16.9 3.5 16.4 3.3 15.9L1.9 11.6L1.1 6.1C1.1 5.8 1.3 5.5 1.6 5.4L2.2 5.3L3.4 10L4 9.9L2.7 4.6L2.5 3.4C2.5 3 2.7 2.8 3 2.7L4 2.4L5.8 9.3L6.5 9.1L4.6 1.8L4.6 1.6C4.6 1.5 4.7 1.4 4.8 1.4L5.9 1C6 1 6 1 6.1 1';
const HAND_LINES = 'M1.9 11.6L1.1 6.1C1.1 5.8 1.3 5.5 1.6 5.4L2.2 5.3L3.4 10L4 9.9L2.7 4.6L2.5 3.4C2.5 3 2.7 2.8 3 2.7L4 2.4L5.8 9.3L6.5 9.1L4.6 1.8L4.6 1.6C4.6 1.5 4.7 1.4 4.8 1.4L5.9 1C6.2 1 6.5 1.1 6.6 1.4L8.8 8.4L9.5 8.2L7.6 2.1L8.4 1.9C8.7 1.8 9.1 1.9 9.2 2.2L12.4 10.4C12.4 10.5 12.5 10.6 12.7 10.6C12.8 10.6 12.9 10.6 13 10.4L14 8.4C14.1 8.2 14.2 8.2 14.4 8.1C14.5 8.1 14.6 8.1 14.8 8.2L15.3 8.5C15.5 8.6 15.6 8.9 15.5 9.1L13.7 15C13.6 15.3 13.5 15.7 13.2 16L12.5 16.8C12.4 16.9 12.4 17.1 12.5 17.2L12.7 17.9L6.4 19.7L6.2 19.1C6.2 19 6.1 19 6.1 18.9L4.3 17.3C3.8 16.9 3.5 16.4 3.3 15.9ZM1.2 11.8L2.6 16.1C2.9 16.8 3.3 17.4 3.8 17.8L5.5 19.4L5.8 20.3C5.9 20.5 6.1 20.6 6.2 20.5L13.2 18.5C13.4 18.4 13.5 18.2 13.4 18L13.2 17.1L13.7 16.4C14.1 16.1 14.3 15.6 14.4 15.2L16.2 9.3C16.4 8.8 16.1 8.2 15.7 7.9L15.1 7.6C14.8 7.4 14.5 7.3 14.2 7.4C13.9 7.5 13.6 7.8 13.4 8.1L12.7 9.4L9.8 2C9.6 1.3 8.9 1 8.2 1.2L7.4 1.4L7.3 1.1C7.3 1.1 7.3 1.1 7.3 1.1L7.3 1L7.2 1C7 0.5 6.3 0.2 5.7 0.4L4.6 0.7C4.2 0.8 4 1.1 3.9 1.4C3.9 1.5 3.8 1.5 3.9 1.6L3.9 1.6L3.8 1.6L3.8 1.7L2.8 2C2.1 2.2 1.7 2.8 1.8 3.5L2 4.6L1.4 4.8C0.8 5 0.4 5.6 0.5 6.2ZM1.2 11.8';

/** The pair of hands beside a weapon section; `used` hands are filled in. The inventory weapons' smaller pair has a lighter outline (`light`). */
export function Hands({ x, y, scale = 1, used = 0, light }: { x: number; y: number; scale?: number; used?: number; light?: boolean }) {
  return (
    <g className={`cs-hands${light ? ' cs-hands--light' : ''}`} transform={`translate(${x} ${y}) scale(${scale})`} role="img" aria-label={`Hands used: ${used}`}>
      {[0, 1].map((i) => (
        <g key={i} className={`cs-hand${i < used ? ' is-used' : ''}`} transform={i === 0 ? undefined : 'translate(35.1 0) scale(-1 1)'}>
          <path className="cs-hand__palm" d={HAND_PALM} />
          <path className="cs-hand__edge" d={HAND_EDGE} />
          <path className="cs-hand__lines" d={HAND_LINES} />
        </g>
      ))}
    </g>
  );
}

/** The site's cut-corner ticket every sheet button sits on. */
const TICKET = '-4 -6.4 6.4 -6.4 6.4 4 4 6.4 -6.4 6.4 -6.4 -4';

/** The sheet's gear, drawn like `EditGears`, for editable HTML inside a page (the Warlock's patron lines). */
export function GearMark({ label }: { label: string }) {
  return (
    <svg className="cs-gear cs-gear--mark" viewBox="-7 -7 14 14" aria-hidden="true">
      <title>{label}</title>
      <polygon points={TICKET} />
      <Settings x={-4.2} y={-4.2} width={8.4} height={8.4} />
    </svg>
  );
}

function SheetButton({ x, y, label, onClick, children }: { x: number; y: number; label: string; onClick: () => void; children: ReactNode }) {
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onClick();
  };
  return (
    <g className="cs-gear" role="button" tabIndex={0} aria-label={label} transform={`translate(${x} ${y})`} onClick={onClick} onKeyDown={onKey}>
      <title>{label}</title>
      {/* The site's cut-corner ticket */}
      <polygon points={TICKET} />
      {children}
    </g>
  );
}

/** Small gear buttons that open the builder step for an area of the sheet. */
export function EditGears({ at, onEdit }: { at: Partial<Record<SheetSection, [number, number]>>; onEdit?: (s: SheetSection) => void }) {
  if (!onEdit) return null;
  return (
    <>
      {(Object.entries(at) as Array<[SheetSection, [number, number]]>).map(([s, [x, y]]) => (
        <SheetButton key={s} x={x} y={y} label={`Edit ${SECTION_NAMES[s]}`} onClick={() => onEdit(s)}>
          <Settings x={-4.2} y={-4.2} width={8.4} height={8.4} aria-hidden="true" />
        </SheetButton>
      ))}
    </>
  );
}

/** The arrow beside the Level shield that starts a level up. */
export function LevelButton({ x, y, onClick }: { x: number; y: number; onClick: () => void }) {
  return (
    <SheetButton x={x} y={y} label="Level up" onClick={onClick}>
      <ChevronsUp x={-4.4} y={-4.4} width={8.8} height={8.8} aria-hidden="true" />
    </SheetButton>
  );
}
